import { Bonus } from '../../types/bonus';

/**
 * Trial welcome recognition Bizy gives on self-serve signup. Source of truth is
 * the backend (`TierAlphaInstaller#create_welcome_recognition`): a `+10 @admin …
 * #teamwork` bonus from "Bizy Bee" to the signing-up admin. Mirrored here so the
 * demo types the same message and the feed can find/hide the post until "sent".
 * Split in two so the demo can pause for Bizy to celebrate after the opening
 * line; the concatenation must stay byte-for-byte identical to the Rails copy.
 */
export const TRIAL_WELCOME_MESSAGE_INTRO = 'Thanks for signing up to help your team work better together!';
export const TRIAL_WELCOME_MESSAGE_REST =
	" I'm Bizy, your AI assistant -- click my icon in the sidebar to talk to me. I can help you get started, answer questions, and even send your first recognition.";
export const TRIAL_WELCOME_MESSAGE = `${TRIAL_WELCOME_MESSAGE_INTRO}${TRIAL_WELCOME_MESSAGE_REST}`;

export const TRIAL_WELCOME_AMOUNT = 10;
export const TRIAL_WELCOME_HASHTAG = 'teamwork';

/**
 * Min viewport width for the demo (the Bizy give-box flight is desktop-only,
 * mirroring the give box's `DRAFT_ASSIST_MOBILE_CUTOFF`). Below this the founding
 * admin just sees their welcome post in the feed — no demo.
 */
export const RECOGNITION_DEMO_MIN_WIDTH_PX = 1240;

/**
 * Server-persisted flag marking that Bizy has "sent" the welcome via the demo.
 * Until set, the welcome post is hidden so the demo can reveal it.
 */
export const TRIAL_WELCOME_DISMISSAL = { namespace: 'recognition_demo', name: 'trial_welcome_sent' } as const;

// Username-agnostic fragment of the welcome reason. The feed API exposes no
// `sample_data` flag or reliable giver name, so matching this distinctive copy
// is the most robust way to identify the post.
const WELCOME_REASON_FINGERPRINT = 'thanks for signing up to help your team';

/** Identifies the Bizy trial welcome post in feed data by its distinctive copy. */
export const isTrialWelcomeBonus = (bonus: Pick<Bonus, 'reason'>): boolean => {
	return bonus.reason?.toLowerCase().includes(WELCOME_REASON_FINGERPRINT) ?? false;
};

/**
 * True when `bonus` is the welcome recognition addressed to the given user. The
 * backend only creates it for the founding admin, so "I'm the recipient" is the
 * founding-admin signal — scoping the demo and feed-hide to that one person.
 */
export const isTrialWelcomeBonusForRecipient = (bonus: Pick<Bonus, 'reason' | 'receivers'>, recipientUserId: string | undefined): boolean => {
	if (!recipientUserId || !isTrialWelcomeBonus(bonus)) return false;
	return bonus.receivers?.some((receiver) => receiver.id === recipientUserId) ?? false;
};
